import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { Link } from 'react-router';
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from "@hookform/resolvers/yup"
import { LoginUserClient, LoginAdminClient } from '../../utility/apiClients/user/loginClient';
import featureControlClient from '../../utility/apiClients/account/featureControlClient';
import NavigationUtil from '../../utility/utils/navigationUtil';

import { encryptData } from '../../utility/utils/hashing';
import { setLogin, setCustomerInfo, setUserDetails, setInvoiceCustomerInfo } from '../../reduxStore/login/loginSlice';
import { setLeadAddressInCookie } from "../../utility/features/helpers/leadHelper";
import OverlayLoader from '../loader/overlayLoader';

import {IsLoggedIn} from '../../utility/features/helpers/authHelper';
import {setSelectedServiceAddress} from "../../reduxStore/myAccount/accountSummarySlice";
import { customerServiceNumberFormatted } from '../../utility/utils/formatter';

import loginCSS from "../../styles/login.module.scss";
import GlobalError from '../globalError/GlobalError';
import missionStatusClient from '../../utility/apiClients/user/missionStatusClient';
import MissionStatusChecker from '../missionStatusChecker';


const Login = () => {

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { userData } = useSelector((state) => state.login);

    const [userInfo, setUserInfo] = useState({ username: '', password: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [missionDown, setMissionDown] = useState(false);

    const { username, password } = userInfo;
    const { custEmail, isRememberMe } = userData;

    const featureControl = async (customerNums) => {
        const screenWidth = global.window && window.innerWidth;
        const device = screenWidth > 768 ? "Web" : "Mobile Web";
        try {

            let defaultFlags = [
                "ECOMSSC"
            ];
            const featureRes = await featureControlClient(customerNums, [], device, [], defaultFlags);

            if (featureRes.data.details.customerNumberSummaryFeatureCode.length !== 0 && window.localStorage) {
                const { customerNumberSummaryFeatureCode } = featureRes.data.details;
                let ECOMSSC;
                customerNumberSummaryFeatureCode.forEach(fc => {
                    if (fc.featureCode === "ECOMSSC") {
                        ECOMSSC = fc;
                    }
                });
                /* SSC */
                let SSCFlagObj = {};
                SSCFlagObj["ECOMSSCFlag"] = (ECOMSSC.featureStatus === "N") ? false : true;
                localStorage.setItem("SSCFlagObj", JSON.stringify(SSCFlagObj));
            }
        } catch (error) {}
    }

    // Shared handling for both the regular login form submit and the admintoken auto-login flow.
    const handleLoginResponse = async (res) => {
        if (res.status !== 200) {
            setError("username", { type: "manual", message: res.error || "Login failed. Please try again." });
            return;
        }

        switch (res.data.code) {
            case 20001: setError("username", { type: "manual", message: res.data.message });
                return;
            case 20003: setError("username", { type: "manual", message: res.data.message });
                return;
            default: break;
        }

        if(res.data?.LoginInfo?.customer_type.toLowerCase() !== "commercial"){
            setError("username", { type: "manual", message: `This TruGreen account is a Residential account. Please <a href="${process.env.REACT_APP_RESI_URL}/my-account/login" className="ml-10"> Click Here</a> to login to Residential portal.` });
            return;
        }

        localStorage.setItem("cusToken", JSON.stringify(res.data?.LoginInfo?.custoken));
        dispatch(setLogin({ ...userData, isLoggedIn: true }));

        document.cookie = `persistent=${res.data?.LoginInfo?.LoginFirstName}; path=/; SameSite=None; Secure; domain=.trugreen.com; max-age=1200`;

        const customerNums = [];
        const businessNames = [];
        res.data.Customers.forEach(c => customerNums.push(c.CustomerNumber));
        res.data.Customers.forEach(c => businessNames.push(c.BusinessName));
        localStorage.setItem("customerNumbers", JSON.stringify(customerNums));
        localStorage.setItem("tg_cust", JSON.stringify(businessNames));
        const zipCodes = [];
        const Customers_IB_Statuses = [];
        res?.data?.Customers?.forEach(c => zipCodes.push(c.ServiceAddresses?.[0]?.postalCode));
        res?.data?.Customers?.forEach(c => Customers_IB_Statuses.push({[c.CustomerNumber] : `${c.IBStatus === "ACTIVE" ? true : false}`}));

        const serviceAddressesWithCustomerNumbers = [];
        res.data.Customers.forEach(c => {
            if(c.ServiceAddresses && c.ServiceAddresses.length > 0){
                c.ServiceAddresses.forEach(s => serviceAddressesWithCustomerNumbers.push(s.address+" | "+c.CustomerNumber));
            } 
        });

        dispatch(setCustomerInfo(res.data?.Customers));
        dispatch(setInvoiceCustomerInfo(res.data?.Customers));

        dispatch(setUserDetails({
            cusToken: res.data?.LoginInfo?.custoken,
            firstName: res.data?.LoginInfo?.LoginFirstName,
            lastName: res.data?.LoginInfo?.LoginLastName,
            Customers_IB_Statuses: JSON.stringify(Customers_IB_Statuses),
            zipCodes: JSON.stringify(zipCodes)
        }));

        /* customer number - service address */
        dispatch(setSelectedServiceAddress(serviceAddressesWithCustomerNumbers[0]));
        localStorage.setItem("serviceAddressesWithCustomerNumbers", JSON.stringify(serviceAddressesWithCustomerNumbers));

        /* ========== save selected service address in leadAddressCookie ========== */
        const serviceAddress = res.data?.Customers[0]?.ServiceAddresses[0];
        const completeAddress = `${serviceAddress && serviceAddress.address ? serviceAddress.address : ""}
                                 ${serviceAddress && serviceAddress.city ? ", " + serviceAddress.city : ""}
                                 ${serviceAddress && serviceAddress.state ? " " + serviceAddress.state : ""}
                                 ${serviceAddress && serviceAddress.zip ? " " + serviceAddress.zip : ""}`;

        setLeadAddressInCookie(completeAddress);

        await featureControl(customerNums);

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            'event': 'account_action',
            'category': 'Account',
            'action': 'Login Button',
            'label': 'success',
            'customer_id': res.data?.Customers[0]?.CustomerNumber || '',
            'error': {
                'reason': '',
            }
        });

        // redirect to invocies page
        navigate(NavigationUtil.decodeRedirect("/my-invoices"), { replace: true });
    };

    // Fetch admintoken param if it is available on the page load , if not ignore it.
    // Call LoginAdminClient if token is available.
    const [searchParams] = useSearchParams();
    const token = useParams().admintoken || searchParams.get("admintoken");
    useEffect(() => {
        if (token) {
            const loginAdmin = async () => {
                setIsLoading(true);
                try {
                    const response = await LoginAdminClient(token);
                    await handleLoginResponse(response);
                } catch (error) {
                    console.error("Admin Login Error", error);
                } finally {
                    setIsLoading(false);
                }
            };
            loginAdmin();
        }
    }, [token]);


    const formSchema = yup.object().shape({
        username: yup.string().email("*Invalid Email").required("Username is required").typeError("Enter a valid email").matches(/^.+@.+\..+/, { message: "Enter a valid email", excludeEmptyString: true }).label("Username"),
        password: yup.string().required('Password is required').min(8, 'Password must be at least 8 characters long').label("Password"),
        remember: yup.boolean()
    });

    const { register, handleSubmit,setValue, setError, formState: { errors } } = useForm({
        mode: 'onChange',
        reValidateMode: 'onBlur',
        resolver: yupResolver(formSchema)
    });

    const loginFormHandler = (e) => {
        const { name, value } = e.target;
        setUserInfo({ ...userInfo, [name]: value });
        setValue(name, value);
    }

    const missionStatusCheckBeforeLogin = async () => {
        try {
            const missionStatus = await missionStatusClient();
            return !["yes"].includes(String(missionStatus?.data).trim().toLowerCase());
        }
        catch (err) {
            console.error("Error checking mission status before login:", err);
            return false;
        }
    }

    const onSubmit = async (data) => {
        
        setIsLoading(true);
        data.password += `__usertoken${Date.now()}`;
        data.password = encryptData(data.password, "login");
        
        const isMissionAvailable = await missionStatusCheckBeforeLogin();
        console.log(isMissionAvailable, "isMissionAvailable on login attempt");
        if (!isMissionAvailable) {
            setMissionDown(true);
            setIsLoading(false);
            return;
        }

        dispatch(setLogin({ ...userData, custEmail: data.username, isRememberMe: data.remember }));

        try {
            const res = await LoginUserClient(data);
            await handleLoginResponse(res);
        } catch (error) {
            setUserInfo({ ...userInfo, password: '' });
            console.log("Login Error", error);
        }
        finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        dispatch({ type: 'RESET_REDUX_STATE' });
        if (IsLoggedIn()) {
            navigate(NavigationUtil.decodeRedirect("/my-invoices"), { replace: true });
        } else {
            localStorage.removeItem("afterLoginGetCustomerDetails");
            localStorage.removeItem("cusToken");
            localStorage.removeItem("customerNumbers");
            if(localStorage.getItem("SSCFlagObj") !== null)
                localStorage.removeItem("SSCFlagObj");
            localStorage.removeItem('paidInvoices');
        }

        if (isRememberMe && custEmail) {
            setUserInfo({ ...userInfo, username: custEmail });
        }

    }, []);


    return (
        <section>
            {missionDown && <MissionStatusChecker />}
            <GlobalError/>
            {isLoading && <OverlayLoader isOpen={isLoading} />}
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-md-6">

                        <div className={`${loginCSS["commercial-login"]} ${loginCSS["login-bg"]}`}>
                            <div className={loginCSS["login-head"]}>
                                <h1>TruGreen Account Login</h1>
                                <span className="d-inline-block gfs-20 gfw-3 mb-24 text-dark">Enter your username or email address and password to sign in</span>
                            </div>

                            <div className={loginCSS["login-form"]}>
                                <form onSubmit={handleSubmit(onSubmit)} noValidate>

                                    <div className="form-group">
                                        {errors.username && <div className={loginCSS["errorMsg"]} dangerouslySetInnerHTML={{__html: errors.username?.message}}></div>}
                                        <input type="text" id="username" className={`${loginCSS["form-control"]} ${errors.username ? loginCSS["errorInput"] : ''}`} placeholder="Username or email" autoComplete='on' {...register("username")} aria-label="Enter your username" value={username} onChange={loginFormHandler} />
                                    </div>

                                    <div className="form-group">
                                        {errors.password && <div className={loginCSS["errorMsg"]}>{errors.password?.message}</div>}
                                        <input type="password" id="password" className={`${loginCSS["form-control"]} ${errors.password ? loginCSS["errorInput"] : ''}`} placeholder="Password" autoComplete='on' {...register("password")} aria-label="Enter your password" value={password} onChange={loginFormHandler} />
                                    </div>

                                    <div className="mb-5">
                                        <Link to="/forgot-password" className={loginCSS["forgot-pwd"]}>Forgot your password?</Link>
                                    </div>

                                    <div className={`form-check mb-3 ${loginCSS["forgot-pwd-div"] } ${loginCSS["frm-chck-pd-lft"]}`}>
                                        <label className="form-check-label d-flex align-items-center gap-3">
                                            <input className={loginCSS["form-check-input"]} type="checkbox" {...register("remember")} checked={isRememberMe} onClick={(e) => dispatch(setLogin({ ...userData, isRememberMe: e.target.checked }))
                                            } /> Remember me
                                        </label>
                                    </div>

                                    <button type="submit" className={`btn ${loginCSS["login-btn"]} ${loginCSS["btn-primary"]}`}>Login</button>

                                    <div className={`col-12 col-md text-md-center ${loginCSS["commercial-login"]}`}>
                                        <h2 className={`text-md-center ${loginCSS["login-help"]}`}>Need help logging in?</h2>
                                        Call:&nbsp;
                                        <a href={`tel:${customerServiceNumberFormatted}`} className={`${loginCSS["contact-info"]} ${loginCSS["contact-information"]}`}>
                                            {customerServiceNumberFormatted}
                                        </a>
                                    </div>

                                </form>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </section>
    )
}

export default Login;